import {applySearchPatch} from '@/shared/screen/search-patch';
import { useRouter, type AnyRouter } from '@tanstack/react-router';

import type { Navigation } from '@/shared/screen';

/** Caminho com query ou âncora (`/em-construcao?caminho=…`) vai como `href`, que o roteador decompõe. */
function hasQuery(to: string): boolean {
  return /[?#]/.test(to);
}

/** Caminho interno do app: uma `/` só no início, sem `\` nem caractere de controle (nunca sai do domínio). */
export function isInternalPath(to: string): boolean {
  return /^\/(?![/\\])/.test(to) && !/[\\\p{Cc}]/u.test(to);
}

/**
 * `Navigation.to` sobre o roteador, aceitando caminho com query (`underConstructionPath`). Recusa
 * esquema, `//` e `\`: com `href` externo o roteador faria navegação de documento (revisão U48, R1).
 */
export function navigateTo(router: AnyRouter, to: string, options?: { search?: Record<string, unknown>; replace?: boolean }) {
  if (!isInternalPath(to)) throw new RangeError('navigateTo: destino fora do app recusado');
  const replace = options?.replace ?? false;
  if (!options?.search && hasQuery(to)) return router.navigate({ href: to, replace });
  return router.navigate({ to, ...(options?.search ? { search: options.search } : {}), replace });
}

/** Partes de um caminho interno para o `Link`: `to` sem query e a busca dele (substitui a atual). */
export function linkTarget(href: string): { readonly to: string; readonly search: () => Record<string, string>; readonly hash?: string } {
  const url = new URL(href, 'http://uyzy.local');
  const search = Object.fromEntries(url.searchParams);
  return { to: url.pathname, search: () => search, ...(url.hash ? { hash: url.hash.slice(1) } : {}) };
}

/**
 * `Navigation` do manifesto (U23) sobre o roteador: é o `nav` que as ações recebem quando a tela
 * roda uma ação. `search` mescla o patch na URL atual; chave com `undefined` sai da URL.
 */
export function useRouterNavigation(): Navigation {
  const router = useRouter();
  return {
    to: (to, options) => navigateTo(router, to, options),
    search: (patch) => router.navigate({ to: '.', search: (prev: Record<string, unknown>) => applySearchPatch(prev,patch) }),
  };
}
